import { View, TouchableOpacity, KeyboardAvoidingView } from "react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"

import FeedbackTextInput from "@/components/input/FeedbackTextInput"
import BackButton from "@/assets/icons/back-arrow-dark.svg"
import { useUser, useUserProfileEmailMutation } from "@/queries/user"
import { router } from "expo-router"
import { PrimaryButton } from "@/components/buttons/CommonButtons"
import { type Dispatch, type SetStateAction, useState } from "react"
import * as AccountEvents from "@/analytics/events/account"
import { useViewedCallback } from "@/analytics/react"
import { setStatusBarStyle } from "@/stores/statusBarStyle"
import useDeviceAttributes from "@/hooks/useDeviceAttributes"
import BFText from "@/components/standard/BFText"

interface EditEmailFormProps {
  email: string
  emailError: string | undefined
  setEmail: Dispatch<SetStateAction<string | null | undefined>>
  disabled?: boolean
}

const EditEmailForm: React.FC<EditEmailFormProps> = ({
  email,
  setEmail,
  emailError,
  disabled = false,
}: EditEmailFormProps) => {
  return (
    <View className="flex flex-col justify-start flex-1">
      <View className="flex flex-col mt-6 shrink-0">
        <View className="py-1">
          <BFText className="text-midnight-blue font-spacegrotesk">
            Email
          </BFText>
        </View>
        <View className="py-1">
          <FeedbackTextInput
            value={email}
            onChange={setEmail}
            error={emailError}
            disabled={disabled}
          />
        </View>
      </View>
    </View>
  )
}

const onBack = () => {
  router.back()
}

export default function EditEmailScreen() {
  const { data, isLoading } = useUser()
  const updateProfileEmailMutation = useUserProfileEmailMutation()
  const insets = useSafeAreaInsets()
  const { isWeb } = useDeviceAttributes()

  useViewedCallback(() => {
    setStatusBarStyle("dark")
    AccountEvents.accountEditEmailViewed()
  }, [])

  const [email, setEmail] = useState<string | null | undefined>(undefined)

  const [emailError, setEmailError] = useState<string | undefined>(undefined)

  if (isLoading || !data?.me) {
    return
  }

  const { me: userProfile } = data

  const scratchEmail = email ?? userProfile.email

  const isDisabled = isLoading || updateProfileEmailMutation.isPending

  const validate = () => {
    let isValid = true
    if (
      typeof scratchEmail === "undefined" ||
      scratchEmail == null ||
      scratchEmail.length === 0
    ) {
      setEmailError("First name is required.")
      isValid = false
    } else {
      setEmailError(undefined)
    }

    return isValid
  }

  const onSave = () => {
    if (isDisabled) {
      return
    }

    if (validate()) {
      updateProfileEmailMutation.mutate(
        {
          email: scratchEmail,
        },
        {
          onSuccess: (data) => {
            if (data?.__typename === "UserProfileUpdateSuccess") {
              router.navigate("/account/personal-info/")
            }

            if (data?.__typename === "UserProfileUpdateError") {
              setEmailError(data.errorText)
            }
          },
          onError: (error) => {
            console.log(error)
          },
        }
      )
    }
  }

  return (
    <KeyboardAvoidingView
      className="flex flex-col justify-start flex-1 bg-white"
      behavior="padding"
      style={{
        paddingTop: isWeb ? insets.top + 10 : insets.top,
      }}
    >
      <View className="flex flex-col px-8 shrink-0">
        <TouchableOpacity testID={"back-button"} onPress={onBack}>
          <BackButton />
        </TouchableOpacity>
        <BFText
          className={`text-[24px] font-figtree-bold leading-[24px] text-midnight-blue pt-4`}
        >
          Edit Email
        </BFText>
      </View>
      <View className="flex flex-col justify-between flex-1 h-full px-8 pb-8">
        <EditEmailForm
          email={scratchEmail}
          setEmail={setEmail}
          emailError={emailError}
          disabled={isDisabled}
        />
        <View className="flex flex-col flex-1 max-h-14">
          <PrimaryButton testID={"save-button"} title="Save" onPress={onSave} />
        </View>
      </View>
    </KeyboardAvoidingView>
  )
}
